import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Loader2, FileText, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LegalPage = () => {
  const { docType } = useParams();
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Map URL-friendly names to API document types
  const docTypeMapping = {
    'privacy': 'privacy_policy',
    'terms': 'terms_of_use',
    'community-guidelines': 'community_guidelines',
    'safeguarding': 'safeguarding',
    'disclaimer': 'disclaimer',
    'cookie-notice': 'cookie_notice',
    'contact': 'contact'
  };

  useEffect(() => {
    fetchLegalDocument();
  }, [docType]);

  const fetchLegalDocument = async () => {
    setLoading(true);
    setError(null);
    try {
      const apiDocType = docTypeMapping[docType] || docType;
      const response = await axios.get(`${API}/legal/${apiDocType}`);
      setDocument(response.data);
    } catch (err) {
      console.error('Error fetching legal document:', err);
      setError('Failed to load document. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-ssdp-teal animate-spin mx-auto mb-4" />
          <p className="text-ssdp-navy dark:text-gray-300">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <FileText className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-ssdp-navy dark:text-white mb-2">Document Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">{error}</p>
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ssdp-teal hover:underline"
          >
            <ChevronLeft className="w-4 h-4" />
            Return Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-ssdp-navy border-b-4 border-ssdp-teal sticky top-0 z-10 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-ssdp-navy dark:text-gray-300 hover:text-ssdp-teal transition-colors mb-4 font-body"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Pathways
          </Link>
          <h1 className="text-3xl md:text-4xl font-black text-ssdp-navy dark:text-white font-heading">
            {document?.title}
          </h1>
          <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 dark:text-gray-400">
            <span className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              Version {document?.version}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Last Updated: {formatDate(document?.last_updated)}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12"
      >
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8 md:p-12">
          {/* Legal Content - Styled for readability */}
          <div 
            className="legal-content prose prose-lg dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: document?.content }}
          />
        </div>

        {/* Additional Resources */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link 
            to="/legal/contact" 
            className="p-4 bg-ssdp-teal/10 border-2 border-ssdp-teal rounded-xl hover:bg-ssdp-teal/20 transition-colors"
          >
            <h3 className="font-bold text-ssdp-navy dark:text-white mb-1">Questions?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Contact us for more information</p>
          </Link>
          
          {docType !== 'privacy_policy' && (
            <Link 
              to="/legal/privacy" 
              className="p-4 bg-purple-50 dark:bg-purple-900/20 border-2 border-purple-300 dark:border-purple-700 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
            >
              <h3 className="font-bold text-ssdp-navy dark:text-white mb-1">Privacy Policy</h3>
              <p className="text-sm text-gray-600 dark:text-gray-300">How we protect your data</p>
            </Link>
          )}
          
          <Link 
            to="/resources" 
            className="p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-300 dark:border-green-700 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
          >
            <h3 className="font-bold text-ssdp-navy dark:text-white mb-1">Support Resources</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Get help when you need it</p>
          </Link>
        </div>
      </motion.div>

      {/* Custom Styles for Legal Content */}
      <style jsx global>{`
        .legal-content h2 {
          font-size: 1.75rem;
          font-weight: 800;
          color: #1e3a59;
          margin-top: 2rem;
          margin-bottom: 1rem;
          font-family: var(--font-heading);
        }
        
        .dark .legal-content h2 {
          color: #ffffff;
        }
        
        .legal-content h3 {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1e3a59;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
          font-family: var(--font-heading);
        }
        
        .dark .legal-content h3 {
          color: #f3f4f6;
        }
        
        .legal-content h4 {
          font-size: 1.1rem;
          font-weight: 600;
          color: #2d5f8d;
          margin-top: 1.25rem;
          margin-bottom: 0.5rem;
        }
        
        .dark .legal-content h4 {
          color: #93c5fd;
        }
        
        .legal-content p {
          margin-bottom: 1rem;
          line-height: 1.75;
          color: #374151;
        }
        
        .dark .legal-content p {
          color: #d1d5db;
        }
        
        .legal-content ul, .legal-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }
        
        .legal-content li {
          margin-bottom: 0.5rem;
          line-height: 1.6;
        }
        
        .legal-content a {
          color: #14b8a6;
          text-decoration: underline;
        }
        
        .legal-content a:hover {
          color: #0d9488;
        }
        
        .legal-content strong {
          font-weight: 700;
          color: #1e3a59;
        }
        
        .dark .legal-content strong {
          color: #ffffff;
        }
        
        .legal-content em {
          font-style: italic;
          color: #6b7280;
        }
        
        .dark .legal-content em {
          color: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default LegalPage;
